/**
 * Round 9 C1 -- what actually reaches this service.
 *
 * Third round. The page printed "No open findings on this entity" and
 * "IBSES n/a" two screens after the IBS portfolio credited the same
 * service with eight critical findings reaching six MVB IBSes.
 *
 * Both statements were true of the query behind them and false about
 * the estate: the page asked for findings raised DIRECTLY on the
 * service, and the connector feeds raise them on configuration items.
 * A page that says "no open findings" about a service carrying eight
 * critical ones is worse than one that says nothing -- the reader has
 * no reason to doubt it.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useServiceReach } from "twin/entity/useServiceReach";

export function ServiceReachPanel({ serviceId }: { serviceId: string }) {
  const { t } = useTranslation();
  // Round 10 C4: the same hook the rest of the page reads, so no part
  // of it can answer this question differently.
  const reach = useServiceReach(serviceId);
  if (!reach.data) return null;
  const {
    findings_direct: direct,
    findings_via_configuration_items: viaCi,
    ibses,
  } = reach.data;
  const serious = reach.data.findings_via_configuration_items_serious;
  const dependents = reach.data.dependent_services;

  return (
    <section data-testid="service-reach" className="space-y-2">
      {viaCi > 0 && (
        <p
          data-testid="service-reach-findings"
          className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          {direct === 0
            ? t("modules.twin.entity.reach.noneDirect", { count: viaCi })
            : t("modules.twin.entity.reach.andVia", { count: viaCi })}{" "}
          {serious != null && serious !== viaCi && (
            <span data-testid="service-reach-serious">
              {t("modules.twin.entity.reach.serious", { count: serious })}{" "}
            </span>
          )}
          <Link
            to={`/twin/findings?service=${serviceId}`}
            className="font-semibold underline"
          >
            {t("modules.twin.entity.reach.seeThem")}
          </Link>
        </p>
      )}
      <div data-testid="service-reach-ibses">
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("modules.twin.entity.reach.ibsesHeading")}
        </h2>
        {dependents != null && (
          <p data-testid="service-reach-cut" className="mb-1 text-sm text-slate-700">
            {dependents > 0
              ? t("modules.twin.entity.reach.cut", {
                  direct: reach.data.ibses_direct_admitted ?? 0,
                  all: reach.data.ibses_reached_admitted ?? 0,
                  count: dependents,
                })
              : t("modules.twin.entity.reach.cutNone", {
                  direct: reach.data.ibses_direct_admitted ?? 0,
                })}
          </p>
        )}
        {ibses.length === 0 ? (
          <p className="text-sm text-slate-600">
            {t("modules.twin.entity.reach.ibsesNone")}
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {ibses.map((i) => (
              <li key={i.id}>
                <Link
                  to={`/twin/ibs/${i.id}`}
                  className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs text-brand-brassDark hover:underline"
                >
                  {i.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
